import { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import AddCommiteeData from '../../../components/addUser/AddCommittee.component';
import SuccessConfirmation from '../../../components/addUser/SuccessConfirmation.component';
import { createNewUser } from '../../../services/users/users.service';
import { UserDto } from '../../../models/userDto.types';
import { UserM, AccountTypeEnum } from '../../../models/user.types';
import AddPersonalData from '../../../components/addUser/AddPersonalData.component';
import usePermission from '../../../hooks/usePermission';
import { UserPermission } from '../../../enum/userPermissions';
import { AxiosResponse } from 'axios';
import { ButtonOne } from '../../../components/ButtonOne/ButtonOne.component';
import { useStyles } from './UserCreate.styles';
import { useTranslation } from 'react-i18next';
import { useLayoutContext } from '../../../Context/Layout.context';

interface AddUserProps {
  userAdded: () => void;
}

export default function UserCreate(props: AddUserProps) {
  const { t } = useTranslation();
  const { openAlert } = useLayoutContext();
  const [open, setOpen] = useState<boolean>(false);

  const [name, setName] = useState<string>('');
  const [surname, setSurname] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [phoneHidden, setPhoneHidden] = useState<boolean>(true);
  const [pesel, setPesel] = useState<string>('');
  const [mail, setMail] = useState<string>('');
  const [commitee, setCommitee] = useState<string>('');
  const [accountType, setAccountType] = useState<AccountTypeEnum | null>(null);
  const [expDate, setExpDate] = useState<Date | null>(null);

  const [stage1, setStage1] = useState<boolean>(false);
  const [stage2, setStage2] = useState<boolean>(false);
  const [stage3, setStage3] = useState<boolean>(false);

  const [close1] = useState<boolean>(false);
  const [close2] = useState<boolean>(false);
  const [close3] = useState<boolean>(false);

  const [, setIsSuccess] = useState<boolean>(false);
  const [temporaryScreen] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);

  const classes = useStyles();
  const handleClickOpen = () => {
    setOpen(true);
    setName('');
    setSurname('');
    setPesel('');
    setMail('');
    setPhone('');
    setPhoneHidden(true);
    setCommitee('');
    setAccountType(null);
    setExpDate(new Date());
  };

  const saveUser = (_commitee: number, _accountType: AccountTypeEnum | null, _expDate?: Date | null) => {
    const dto: UserDto = {
      firstName: name,
      lastName: surname,
      pesel: pesel,
      phone: phone,
      phoneHidden: phoneHidden,
      email: mail,
      accountTypeId: _accountType,
      expirationDate: _expDate,
      examCommitteeId: _commitee,
    };
    createNewUser(dto).then(
      (result: AxiosResponse<UserM>) => {
        result.status == 201 ? setIsSuccess(true) : setIsSuccess(false);
        setIsSubmit(true);
        props.userAdded();
      },
      () => {
        if (!navigator.onLine) {
          openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
        } else openAlert('save_error');
        setOpen(false);
      },
    );
  };

  const personalDataToParent = (
    _name: string,
    _surname: string,
    _pesel: string,
    _phone: string,
    _phoneHidden: boolean,
    _mail: string,
    _close: boolean,
    _toStage2: boolean,
  ) => {
    setName(_name);
    setSurname(_surname);
    setPhone(_phone);
    setPhoneHidden(_phoneHidden);
    setPesel(_pesel);
    setMail(_mail);
    setOpen(!_close);

    if (_close) {
      setStage1(false);
      setStage2(false);
      setStage3(false);
    } else {
      setStage1(!_toStage2);
      setStage2(_toStage2);
      setStage3(!_toStage2);
    }
  };

  const commiteeDataToParent = (
    _commitee: string,
    _accType: AccountTypeEnum | null,
    _expDate: Date | null,
    _toStage3: boolean,
    _close: boolean,
    _previous: boolean,
  ) => {
    setCommitee(_commitee);
    setAccountType(_accType);
    setExpDate(_expDate);
    setOpen(!_close);

    if (_close) {
      setStage1(false);
      setStage2(false);
      setStage3(false);
    } else if (_previous) {
      setStage2(false);
      setStage1(true);
    } else {
      setStage1(!_toStage3);
      setStage3(_toStage3);
      setStage2(!_toStage3);
      saveUser(parseInt(_commitee), _accType, _expDate);
    }
  };

  const successDataToParent = (_close: boolean) => {
    setOpen(!_close);
    setStage1(!_close);
    setStage2(!_close);
    setStage3(!_close);
  };

  const permission = usePermission(UserPermission.AdminUserCreate);

  return (
    <div>
      <ButtonOne
        className={classes.addUserButton}
        variant="contained"
        color="primary"
        disabled={!permission}
        onClick={() => {
          setStage1(true);
          setStage2(false);
          setStage3(false);
          handleClickOpen();
        }}
      >
        {permission ? <span style={{ margin: '0px 10px 0px -4px', fontSize: '20px' }}>+</span> : ''}
        {t('users:header.addUserButton')}
      </ButtonOne>
      <Dialog
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            setOpen(false);
          }
        }}
        aria-labelledby="dodaj użytkownika"
        open={open}
      >
        {stage1 && (
          <AddPersonalData
            nameArg={name}
            surnameArg={surname}
            peselArg={pesel}
            phoneArg={phone}
            phoneHiddenArg={phoneHidden}
            mailArg={mail}
            closeArg={close1}
            toStage2={stage2}
            personalDataToParent={personalDataToParent}
          />
        )}
        {stage2 && accountType !== undefined && (
          <AddCommiteeData
            isExaminerReadOnly={false}
            commiteeArg={commitee}
            accTypeArg={accountType}
            expDateArg={expDate}
            closeArgm={close2}
            toStage3={stage3}
            commiteeDataToParent={commiteeDataToParent}
          />
        )}
        {stage3 && isSubmit && (
          <SuccessConfirmation
            temporaryArg={temporaryScreen}
            isSuccessArg={true}
            closeArg={close3}
            mail={mail}
            successDataToParent={successDataToParent}
          />
        )}
      </Dialog>
    </div>
  );
}
